import React, { useEffect, useState } from 'react';
import { ArrowLeft, Check, Clock3, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { persistLocalBookingProfile, syncPublicUserProfile } from '../lib/bookingService';

const NAME_CHANGE_LOCK_KEY = 'echo_jham_booking_name_last_changed_at';
const SIX_MONTHS_MS = 183 * 24 * 60 * 60 * 1000;

const getLastNameChange = () => Number(localStorage.getItem(NAME_CHANGE_LOCK_KEY) || 0);

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-PH', { timeZone: 'Asia/Manila' });
};

export default function ChangeNamePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [currentName, setCurrentName] = useState('');
  const [newName, setNewName] = useState('');

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const cachedEmail = localStorage.getItem('userEmail');
        const cachedName = localStorage.getItem('userName');

        const {
          data: { user },
        } = await supabase.auth.getUser();

        const email = user?.email || cachedEmail || '';
        const name =
          user?.user_metadata?.full_name ||
          user?.user_metadata?.name ||
          cachedName ||
          (email ? email.split('@')[0].replace(/[._-]+/g, ' ') : '');

        if (!email && !localStorage.getItem('userAuthenticated')) {
          navigate('/login', { replace: true });
          return;
        }

        if (email) {
          localStorage.setItem('userEmail', email);
        }

        if (name) {
          localStorage.setItem('userName', name);
        }

        setUserEmail(email);
        setCurrentName(name);
        setNewName(name);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [navigate]);

  const lastChangeDate = getLastNameChange();
  const lockActive = lastChangeDate && Date.now() - lastChangeDate < SIX_MONTHS_MS;
  const remainingMs = lockActive ? SIX_MONTHS_MS - (Date.now() - lastChangeDate) : 0;
  const remainingDays = lockActive ? Math.max(1, Math.ceil(remainingMs / (24 * 60 * 60 * 1000))) : 0;

  const handleSave = async () => {
    const trimmedName = newName.trim();
    if (!trimmedName) {
      setError('Name is required.');
      setMessage('');
      return;
    }

    if (lockActive) {
      setError('You can only change your name once every 6 months.');
      setMessage('');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        data: { full_name: trimmedName },
      });

      if (updateError) throw updateError;

      const {
        data: { user: updatedUser },
      } = await supabase.auth.getUser();

      await syncPublicUserProfile({
        id: updatedUser?.id,
        email: updatedUser?.email || userEmail,
        full_name: trimmedName,
      });

      persistLocalBookingProfile({
        email: updatedUser?.email || userEmail,
        name: trimmedName,
      });
      localStorage.setItem(NAME_CHANGE_LOCK_KEY, String(Date.now()));
      setCurrentName(trimmedName);
      setMessage('Name updated successfully.');
      navigate('/profile/edit');
    } catch (updateErr) {
      setError(updateErr.message || 'Unable to update name.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-[calc(100dvh-8rem)] overflow-hidden bg-[#141414] text-white grid place-items-center px-6 py-6">
        <p className="text-sm text-white/60">Loading name editor...</p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100dvh-8rem)] overflow-hidden bg-[#141414] text-white px-5 py-4 md:py-6">
      <div className="mx-auto flex h-full max-w-sm flex-col items-center justify-start pt-3 md:pt-4">
        <div className="w-full">
          <button
            type="button"
            onClick={() => navigate('/profile/edit')}
            className="inline-flex items-center gap-2 text-sm font-medium text-white/80 transition hover:text-white"
          >
            <ArrowLeft size={18} />
            Back
          </button>

          <div className="mt-2 text-center">
            <h1 className="text-[28px] font-semibold tracking-tight text-white">Change Name</h1>
          </div>

          <div className="mt-4 flex flex-col items-center text-center">
            <div className="grid h-24 w-24 place-items-center rounded-full border-2 border-white bg-transparent">
              <div className="grid h-16 w-16 place-items-center rounded-full bg-white text-[#141414]">
                <UserRound size={34} strokeWidth={1.75} />
              </div>
            </div>

            <h2 className="mt-2 text-[26px] font-semibold leading-tight tracking-tight text-white">
              {currentName || 'Your Profile'}
            </h2>
            <p className="mt-2 text-[13px] text-white/55">{userEmail || 'No email available'}</p>

            <p className="mt-2 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-medium text-white/70">
              <Clock3 size={12} />
              You can only change your name once every 6 months.
            </p>

            {lockActive ? (
              <p className="mt-3 text-xs text-amber-200">
                Last changed on {formatDate(lastChangeDate)}. You can try again in about {remainingDays} day(s).
              </p>
            ) : (
              <p className="mt-3 text-xs text-white/45">Edit your display name below and save it once.</p>
            )}
          </div>

          <div className="mt-3 rounded-[18px] bg-[#5b5b5b] px-4 py-4 shadow-[0_10px_20px_rgba(0,0,0,0.24)]">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-white/45">
              Name
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={lockActive}
              className="w-full rounded-[14px] border border-white/10 bg-[#1a1a1a] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-white/15 disabled:cursor-not-allowed disabled:opacity-60"
              placeholder="Enter your name"
            />

            <button
              type="button"
              onClick={handleSave}
              disabled={saving || lockActive}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-[14px] bg-[#1e6fff] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#125ee6] disabled:opacity-60"
            >
              <Check size={16} />
              {saving ? 'Saving...' : 'Save Name'}
            </button>
          </div>

          {message ? <p className="mt-4 text-xs font-medium text-emerald-300">{message}</p> : null}
          {error ? <p className="mt-4 text-xs font-medium text-rose-300">{error}</p> : null}
        </div>
      </div>
    </div>
  );
}